import React, { useState, useCallback } from "react";
import { PlusIcon } from "../../SVGIcons";

interface NewTemplateFormProps {
  newTemplateName: string;
  setNewTemplateName: (name: string) => void;
  newTemplatePosition: 'top' | 'bottom';
  setNewTemplatePosition: (position: 'top' | 'bottom') => void;
  newTemplatePreview: string | null;
  onFileChange: (file: File) => void;
  editingTemplateId: number | null;
  isSavingTemplate: boolean;
  onCancel: () => void;
  onSave: () => void;
  onUpdate: () => void;
  newTemplateFile: File | null;
}

export const NewTemplateForm: React.FC<NewTemplateFormProps> = ({
  newTemplateName,
  setNewTemplateName,
  newTemplatePosition,
  setNewTemplatePosition,
  newTemplatePreview,
  onFileChange,
  editingTemplateId,
  isSavingTemplate,
  onCancel,
  onSave,
  onUpdate,
  newTemplateFile,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.match(/^image\/(png|jpeg|webp)$/)) {
      onFileChange(file);
    }
  }, [onFileChange]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          width: '460px',
          maxWidth: '90%'
        }}
      >
        <h3 style={{ fontSize: 18, fontWeight: 600, color: '#333', marginBottom: 24 }}>
          {editingTemplateId ? 'テンプレートを編集' : '新規テンプレート登録'}
        </h3>

        {/* 画像プレビュー */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 14, color: '#444', marginBottom: 8, fontWeight: 500 }}>
            帯画像
          </label>
          {newTemplatePreview ? (
            <div
              style={{ position: 'relative' }}
              {...(!editingTemplateId ? {
                onDragOver: handleDragOver,
                onDragLeave: handleDragLeave,
                onDrop: handleDrop,
              } : {})}
            >
              <img
                src={newTemplatePreview}
                alt="プレビュー"
                style={{
                  width: '100%',
                  height: 80,
                  objectFit: 'contain',
                  borderRadius: 8,
                  border: isDragOver && !editingTemplateId ? '2px dashed #3b82f6' : '1px solid #e5e7eb',
                  background: isDragOver && !editingTemplateId ? '#eff6ff' : '#f3f4f6',
                  transition: 'border-color 0.2s, background 0.2s'
                }}
              />
              {!editingTemplateId && (
                <label
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: 4,
                    fontSize: 12,
                    cursor: 'pointer'
                  }}
                >
                  変更
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/webp"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        onFileChange(e.target.files[0]);
                      }
                    }}
                  />
                </label>
              )}
            </div>
          ) : (
            <label
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 120,
                border: isDragOver ? '2px dashed #3b82f6' : '2px dashed #d1d5db',
                borderRadius: 8,
                cursor: 'pointer',
                background: isDragOver ? '#eff6ff' : '#f9fafb',
                transition: 'border-color 0.2s, background 0.2s'
              }}
            >
              <PlusIcon style={{ width: 24, height: 24, color: isDragOver ? '#3b82f6' : '#9ca3af', marginBottom: 8 }} />
              <span style={{ fontSize: 14, color: isDragOver ? '#3b82f6' : '#6b7280' }}>
                ドラッグ＆ドロップ、またはクリックして画像を選択
              </span>
              <input
                type="file"
                accept="image/png, image/jpeg, image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    onFileChange(e.target.files[0]);
                  }
                }}
              />
            </label>
          )}
        </div>

        {/* テンプレート名 */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 14, color: '#444', marginBottom: 8, fontWeight: 500 }}>
            テンプレート名
          </label>
          <input
            type="text"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value.slice(0, 50))}
            placeholder="例：春の新生活キャンペーン用"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box'
            }}
          />
          <div style={{ fontSize: 12, color: '#999', marginTop: 4, textAlign: 'right' }}>
            {newTemplateName.length}/50文字
          </div>
        </div>

        {/* 配置位置 */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 14, color: '#444', marginBottom: 8, fontWeight: 500 }}>
            配置位置
          </label>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={() => setNewTemplatePosition('top')}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: newTemplatePosition === 'top' ? '2px solid #3b82f6' : '1px solid #ccc',
                borderRadius: 6,
                background: newTemplatePosition === 'top' ? '#eff6ff' : '#fff',
                color: newTemplatePosition === 'top' ? '#1d4ed8' : '#333',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: newTemplatePosition === 'top' ? 600 : 400
              }}
            >
              上部
            </button>
            <button
              type="button"
              onClick={() => setNewTemplatePosition('bottom')}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: newTemplatePosition === 'bottom' ? '2px solid #3b82f6' : '1px solid #ccc',
                borderRadius: 6,
                background: newTemplatePosition === 'bottom' ? '#eff6ff' : '#fff',
                color: newTemplatePosition === 'bottom' ? '#1d4ed8' : '#333',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: newTemplatePosition === 'bottom' ? 600 : 400
              }}
            >
              下部
            </button>
          </div>
        </div>

        {/* ボタン */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSavingTemplate}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #ccc',
              borderRadius: 6,
              background: '#fff',
              color: '#666',
              cursor: isSavingTemplate ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            キャンセル
          </button>
          {editingTemplateId ? (
            /* 編集モード */
            <button
              type="button"
              onClick={onUpdate}
              disabled={!newTemplateName.trim() || isSavingTemplate}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                borderRadius: 6,
                background: newTemplateName.trim() && !isSavingTemplate ? '#3b82f6' : '#ccc',
                color: '#fff',
                cursor: newTemplateName.trim() && !isSavingTemplate ? 'pointer' : 'not-allowed',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              {isSavingTemplate ? '更新中...' : '更新する'}
            </button>
          ) : (
            /* 新規モード */
            <button
              type="button"
              onClick={onSave}
              disabled={!newTemplateFile || !newTemplateName.trim() || isSavingTemplate}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                borderRadius: 6,
                background: newTemplateFile && newTemplateName.trim() && !isSavingTemplate ? '#3b82f6' : '#ccc',
                color: '#fff',
                cursor: newTemplateFile && newTemplateName.trim() && !isSavingTemplate ? 'pointer' : 'not-allowed',
                fontSize: 14,
                fontWeight: 500
              }}
            >
              {isSavingTemplate ? '保存中...' : '登録する'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewTemplateForm;
